import { useEffect } from 'react';

import { Box } from '@/components/Box';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithChildren } from '@/lib/types';

import type { Gallery } from '../context';
import { useCompoundContext } from '../context';

import * as GS from '../ImageCarousel.styles';

export interface CarouselPreviewItemProps extends WithChildren {
  height?: Maybe<string | number>;
  largeSrc?: Maybe<string>;
  width?: Maybe<string | number>;
}

export const CarouselItem = ({
  children,
  height,
  largeSrc,
  width,
}: CarouselPreviewItemProps) => {
  const { gallery, isProcessing } = useCompoundContext();

  useEffect(() => {
    if (gallery && !isProcessing && (!height || !largeSrc || !width)) {
      throw new Error(
        'When gallery prop is passed - height, largeSrc and width must be provided',
      );
    }
  }, [gallery, height, largeSrc, width]);

  if (shouldRenderGallery({ gallery, height, isProcessing, largeSrc, width })) {
    return (
      <GS.PhotoSwipeLink
        data-pswp-height={height}
        data-pswp-width={width}
        data-test-id='CarouselItemPhotoSwipeLink'
        href={largeSrc ?? '#'}
        isProcessing={isProcessing}
        onClick={(evt) => {
          if (isProcessing) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        }}
        rel='noreferrer'
        target='_blank'
      >
        <Box
          {...generateDataTestId('imagesGallery', 'carouselItemWithGallery')}
          data-test-id='CarouselItemBox'
        >
          {children}
        </Box>
      </GS.PhotoSwipeLink>
    );
  } else {
    return (
      <Box
        {...generateDataTestId('imagesGallery', 'carouselItemWithoutGallery')}
        data-test-id='CarouselItemBox'
      >
        {children}
      </Box>
    );
  }
};

interface ShouldRenderGalleryProps {
  gallery: Gallery | undefined;
  height: Maybe<string | number>;
  isProcessing: boolean;
  largeSrc: Maybe<string>;
  width: Maybe<string | number>;
}

const shouldRenderGallery = ({
  gallery,
  height,
  isProcessing,
  largeSrc,
  width,
}: ShouldRenderGalleryProps): boolean =>
  !!gallery && !!height && !isProcessing && !!largeSrc && !!width;
